/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react"
import cn from "classnames"
import usePreRender from "hooks/usePreRender"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import useCombinedScheduleBFF from "hooks/bff_entities/useCombinedScheduleBFF"
import { IoClose, IoSearch, IoChevronDown, IoChevronUp } from "react-icons/io5"
import { FaSave, FaTimes, FaCheck, FaUndo } from "react-icons/fa"
import { CustomSearchSelect } from './CustomSearchSelect'
import { TransferFormData, SourceData, LocationData, ChannelData, RouteData, CourierData } from "../types"
import Swal from "sweetalert2"
import { formatLocationName } from "../../../utils/utils"

interface TransferFormProps {
  selectedCount: number;
  onSubmit: (transferData: TransferFormData, selectedRoute:RouteData, selectedCourier: CourierData, routeSearchMode: 'search' | 'changeSource' | 'changeTarget') => void;
  onCancel: () => void;
}

export const TransferForm: React.FC<TransferFormProps> = ({
  selectedCount,
  onSubmit,
  onCancel
}) => {
  const { _color } = usePreRender()
  
  // Hooks
  const { data: sourcesData, search_osrm_action: searchSources } = useSearchMiddlewareOSRM({ entity: "logistic_sources" })
  const { data: locationsData, search_osrm_action: searchLocations } = useSearchMiddlewareOSRM({ entity: "locations" })
  const { data: channelsData, search_osrm_action: searchChannels } = useSearchMiddlewareOSRM({ entity: "channels" })
  const { data: couriersData, search_osrm_action: searchCouriers } = useSearchMiddlewareOSRM({ entity: "couriers" })
  const { data: routesData, search_osrm_action: searchRoutes } = useSearchMiddlewareOSRM({ entity: "routes" })
  const [routeSearchMode, setRouteSearchMode] = useState<'search' | 'changeSource' | 'changeTarget'>('search');
  const { get_schedule_types_action, set_loading_app } = useCombinedScheduleBFF()

  // Local state
  const [sourceQuery, setSourceQuery] = useState("")
  const [locationQuery, setLocationQuery] = useState("")
  const [channelQuery, setChannelQuery] = useState("")
  const [deliveryTypes, setDeliveryTypes] = useState<string[]>([])
  const [shippingTypes, setShippingTypes] = useState<string[]>([])

  // New state for search functionality
  const [routeSearchQuery, setRouteSearchQuery] = useState("")
  const [courierSearchQuery, setCourierSearchQuery] = useState("")
  const [isRouteFormExpanded, setIsRouteFormExpanded] = useState(false)
  const [isCourierFormExpanded, setIsCourierFormExpanded] = useState(false)
  const [showRouteResults, setShowRouteResults] = useState(false)
  const [showCourierResults, setShowCourierResults] = useState(false)
  const [selectedRoute, setSelectedRoute] = useState<any>(null)
  const [selectedCourier, setSelectedCourier] = useState<any>(null)
  const [routePage, setRoutePage] = useState(1)
  const [courierPage, setCourierPage] = useState(1)
  const [isSearchingRoutes, setIsSearchingRoutes] = useState(false)
  const [isSearchingCouriers, setIsSearchingCouriers] = useState(false)

  // Form data
  const [transferData, setTransferData] = useState<TransferFormData>({
    route: {
      query: "",
      source: {
        type: "Source",
        data: {
          id: "",
          alias: "",
          name: ""
        }
      },
      target: {
        type: "Location",
        data: {
          criteria0: "",
          criteria1: "",
          criteria2: "",
          criteria3: "",
          criteria4: ""
        }
      }
    },
    courier: {
      query: "",
      name: "",
      alias: "",
      shippingType: "",
      deliveryType: "",
      channel: ""
    },
    leadtime: {
      value: 1,
      unit: 'd'
    },
    deliveryDays: [1, 2, 3, 4, 5],
    workingTime: [
      { from: "08:00", to: "17:00" }
    ]
  })

  // Data
  const endpointTypes = ["Source", "Location"]
  const sources: SourceData[] = sourcesData?.records || []
  const locations: LocationData[] = locationsData?.records || []
  const channels: ChannelData[] = channelsData?.records || []
  const couriers: CourierData[] = couriersData?.records || []
  const routes: RouteData[] = routesData?.records || []

  // Pagination constants
  const ITEMS_PER_PAGE = 5

  // Effects
  useEffect(() => {
    let filters = sourceQuery ? { "id": sourceQuery } : {}
    searchSources({ from: 0, size: 1000, filters })
  }, [sourceQuery])

  useEffect(() => {
    searchLocations({ from: 0, size: 1000, queryString: locationQuery })
  }, [locationQuery])

  useEffect(() => {
    searchChannels({ from: 0, size: 1000, queryString: channelQuery })
  }, [channelQuery])

  useEffect(() => {
    const fetchScheduleTypes = async () => {
      try {
        set_loading_app(true)
        const response = await get_schedule_types_action("")
        
        if (response) {
          setDeliveryTypes(response.deliveryTypes || [])
          setShippingTypes(response.shippingTypes || [])
        }
      } catch (error) {
        console.error("Error fetching schedule types:", error)
      } finally {
        set_loading_app(false)
      }
    }
    
    fetchScheduleTypes()
  }, [])

  // Handlers
  const handleInputChange = (path: string, value: any) => {
    const pathArray = path.split('.')
    const newTransferData = { ...transferData }
    let current: any = newTransferData
    
    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]]
    }
    
    current[pathArray[pathArray.length - 1]] = value
    setTransferData(newTransferData)
  }

  const handleEndpointTypeChange = (path: string, type: "Source" | "Location") => {
    const defaultData = type === "Source" 
      ? { id: "", alias: "", name: "" }
      : { criteria0: "", criteria1: "", criteria2: "", criteria3: "", criteria4: "" }
    
    const newTransferData = { ...transferData }
    let current: any = newTransferData
    const pathArray = path.split('.')
    
    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]]
    }
    
    current[pathArray[pathArray.length - 1]] = {
      type,
      data: defaultData
    }
    
    setTransferData(newTransferData)
  }

  const handleEndpointDataSelect = (path: string, data: any) => {
    const newTransferData = { ...transferData }
    let current: any = newTransferData
    const pathArray = path.split('.')
    
    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]]
    }
    
    current[pathArray[pathArray.length - 1]] = data
    setTransferData(newTransferData)
  }

  // New handlers for route and courier search
  const handleRouteSearch = async () => {
    const routeInfo = transferData.route;

    // Clean up routeInfo empty fields
    console.log(routeInfo)
    const cleanedRouteInfo:any = { ...routeInfo };
    Object.keys(cleanedRouteInfo).forEach((key) => {
      if (
        (cleanedRouteInfo[key] === "" ||
        (typeof cleanedRouteInfo[key] === "object" &&
        Object.values(cleanedRouteInfo[key]).every((value) => value === ""))
      ) ) {
        delete cleanedRouteInfo[key];
      }
    });
    delete cleanedRouteInfo.query;

    // Check if source has valid data
    if (
      cleanedRouteInfo.source &&
      cleanedRouteInfo.source.data &&
      Object.values(cleanedRouteInfo.source.data).every((value) => value === "")
    ) {
      cleanedRouteInfo.source = undefined;
    }

    // Check if target has valid data
    if (
      cleanedRouteInfo.target &&
      cleanedRouteInfo.target.data &&
      Object.values(cleanedRouteInfo.target.data).every((value) => value === "")
    ) {
      cleanedRouteInfo.target = undefined;
    }

    setIsSearchingRoutes(true)
    try {
      await searchRoutes({ 
        from: (routePage - 1) * ITEMS_PER_PAGE, 
        size: ITEMS_PER_PAGE, 
        queryString: !routeSearchQuery.trim() ? undefined : routeSearchQuery,
        filters: cleanedRouteInfo,
      })
      setShowRouteResults(true)
    } catch (error) {
      console.error("Error searching routes:", error)
    } finally {
      setIsSearchingRoutes(false)
    }
  }

  const handleCourierSearch = async () => {
    const courierInfo = transferData.courier;

    // Clean up routeInfo empty fields
    const cleanedCourierInfo:any = { ...courierInfo };
    Object.keys(cleanedCourierInfo).forEach((key) => {
      if (
        ( cleanedCourierInfo[key] === "" ||
        (typeof cleanedCourierInfo[key] === "object" &&
        Object.values(cleanedCourierInfo[key]).every((value) => value === ""))
      ) ) {
        delete cleanedCourierInfo[key];
      }
    });
    delete cleanedCourierInfo.query;
    
    setIsSearchingCouriers(true)
    try {
      await searchCouriers({ 
        from: (courierPage - 1) * ITEMS_PER_PAGE, 
        size: ITEMS_PER_PAGE, 
        queryString: !courierSearchQuery.trim() ? undefined : courierSearchQuery,
        filters: cleanedCourierInfo,
      })
      setShowCourierResults(true)
    } catch (error) {
      console.error("Error searching couriers:", error)
    } finally {
      setIsSearchingCouriers(false)
    }
  }

  const handleRouteSelect = (route: any) => {
    setSelectedRoute(route)
    setShowRouteResults(false)
  }

  const handleCourierSelect = (courier: any) => {
    setSelectedCourier(courier)
    setShowCourierResults(false)
  }

  const handleRouteUnselect = () => {
    setSelectedRoute(null)
    setShowRouteResults(true)
  }

  const handleCourierUnselect = () => {
    setSelectedCourier(null)
    setShowCourierResults(true)
  }

  const handleRoutePageChange = async (newPage: number) => {
    setRoutePage(newPage)
    setIsSearchingRoutes(true)
    try {
      await searchRoutes({ 
        from: (newPage - 1) * ITEMS_PER_PAGE, 
        size: ITEMS_PER_PAGE, 
        queryString: routeSearchQuery 
      })
    } catch (error) {
      console.error("Error changing route page:", error)
    } finally {
      setIsSearchingRoutes(false)
    }
  }

  const handleCourierPageChange = async (newPage: number) => {
    setCourierPage(newPage)
    setIsSearchingCouriers(true)
    try {
      await searchCouriers({ 
        from: (newPage - 1) * ITEMS_PER_PAGE, 
        size: ITEMS_PER_PAGE, 
        queryString: courierSearchQuery 
      })
    } catch (error) {
      console.error("Error changing courier page:", error)
    } finally {
      setIsSearchingCouriers(false)
    }
  }

  const isObjectNotEmpty = (obj: Record<string, any>): boolean => {
    return Object.keys(obj).some(key => {
      const value = obj[key];
      return value !== '' && value !== null && value !== undefined && 
             (!Array.isArray(value) || value.length > 0) && 
             (typeof value !== 'object' || isObjectNotEmpty(value));
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Create confirmation dialog with summary
    const routeInfo = routeSearchMode === 'search' && selectedRoute 
      ? `${('name' in selectedRoute.source?.data ? selectedRoute.source.data.name : (formatLocationName(selectedRoute.source.data))) || 'Origen'} → ${('name' in selectedRoute.target?.data ? selectedRoute.target.data.name : (formatLocationName(selectedRoute.target.data))) || 'Destino'}` : 
      routeSearchMode === 'changeSource' && isObjectNotEmpty(transferData.route.source.data) ? ('name' in transferData.route.source?.data ? transferData.route.source.data.name : (formatLocationName(transferData.route.source.data))) : 
      routeSearchMode === 'changeTarget' && isObjectNotEmpty(transferData.route.target.data) ? ('name' in transferData.route.target?.data ? transferData.route.target.data.name : (formatLocationName(transferData.route.target.data))) : 'No seleccionada';
      
    const courierInfo = selectedCourier 
      ? `${selectedCourier.name || selectedCourier.id}`
      : 'No seleccionado';
      
    const leadtimeInfo = `${transferData.leadtime.value} ${transferData.leadtime.unit === 'd' ? 'día(s)' : 'hora(s)'}`;
    const routeSearchModeLabel = routeSearchMode === 'search' ? 'Cambiar Ruta' :  routeSearchMode === 'changeSource' ? 'Cambiar Origen' :  routeSearchMode === 'changeTarget' ?'Cambiar Destino' : '';
    const result = await Swal.fire({
      title: 'Confirmar Transferencia',
      html: `
        <div style="text-align: left; font-size: 14px;">
          <p><strong>Ruta</strong>(${routeSearchModeLabel}): ${routeInfo}</p>
          <p><strong>Courier:</strong> ${courierInfo}</p>
          <p><strong>Tiempo de entrega:</strong> ${leadtimeInfo}</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Transferir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#4F46E5',
      cancelButtonColor: '#6B7280',
      width: '400px'
    });

    if (result.isConfirmed) {
      onSubmit(transferData, selectedRoute, selectedCourier, routeSearchMode)
    }
  }

  // Render route results
  const renderRouteResults = () => {
    if (!showRouteResults || routes.length === 0) return null

    return (
      <div className="mt-4 bg-yellow-50 border border-gray-200 rounded-md bg-white">
        <div className="p-4">
          <h5 className="text-sm font-medium text-gray-700 mb-3">Rutas encontradas</h5>
          <div className="space-y-2">
            {routes.slice(0, ITEMS_PER_PAGE).map((route, index) => (
              <div
                key={`route-${index}`}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                onClick={() => handleRouteSelect(route)}
              >
                <div>
                  <div className="font-medium text-gray-800">
                    {('name' in route.source.data ? route.source.data.name : 'criteria1' in route.source.data ? formatLocationName(route.source.data) : '')} → {('criteria1' in route.target.data ? formatLocationName(route.target.data) : ('name' in route.target.data ? route.target.data.name : ''))}</div>
                </div>
                <FaCheck className="text-green-500 w-4 h-4" />
              </div>
            ))}          </div>
          
          {/* Pagination for routes */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => handleRoutePageChange(routePage - 1)}
              disabled={routePage === 1 || isSearchingRoutes}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600">Página {routePage}</span>
            <button
              onClick={() => handleRoutePageChange(routePage + 1)}
              disabled={routes.length < ITEMS_PER_PAGE || isSearchingRoutes}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render courier results
  const renderCourierResults = () => {
    if (!showCourierResults || couriers.length === 0) return null

    return (
      <div className="mt-4 bg-yellow-50 border border-gray-200 rounded-md bg-white">
        <div className="p-4">
          <h5 className="text-sm font-medium text-gray-700 mb-3">Couriers encontrados</h5>
          <div className="space-y-2">
            {couriers.slice(0, ITEMS_PER_PAGE).map((courier, index) => (
              <div
                key={`courier-${index}`}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                onClick={() => handleCourierSelect(courier)}
              >
                <div>
                  <div className="font-medium text-gray-800">{courier.name || courier.id}</div>
                </div>
                <FaCheck className="text-green-500 w-4 h-4" />
              </div>
            ))}
          </div>
          
          {/* Pagination for couriers */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => handleCourierPageChange(courierPage - 1)}
              disabled={courierPage === 1 || isSearchingCouriers}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600">Página {courierPage}</span>
            <button
              onClick={() => handleCourierPageChange(courierPage + 1)}
              disabled={couriers.length < ITEMS_PER_PAGE || isSearchingCouriers}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render selected route
  const renderSelectedRoute = () => {
    if (!selectedRoute) return null

    return (
      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FaCheck className="text-green-500 w-4 h-4" />
              <span className="font-medium text-green-800">Ruta seleccionada</span>
            </div>
            <div className="mt-1">
              <div className="font-medium text-gray-800">{selectedRoute.name || selectedRoute.id}</div>
            </div>
          </div>
          <button
            onClick={handleRouteUnselect}
            className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
          >
            <FaUndo className="w-3 h-3" />
            Cambiar
          </button>
        </div>
      </div>
    )
  }

  // Render selected courier
  const renderSelectedCourier = () => {
    if (!selectedCourier) return null

    return (
      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FaCheck className="text-green-500 w-4 h-4" />
              <span className="font-medium text-green-800">Courier seleccionado</span>
            </div>
            <div className="mt-1">
              <div className="font-medium text-gray-800">{selectedCourier.name || selectedCourier.id}</div>
            </div>
          </div>
          <button
            onClick={handleCourierUnselect}
            className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
          >
            <FaUndo className="w-3 h-3" />
            Cambiar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              Transferir Coberturas ({selectedCount} seleccionadas)
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              <IoClose size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Route Section */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-800">Nueva Información de Ruta</h3>
                <button
                  type="button"
                  onClick={() => setIsRouteFormExpanded(!isRouteFormExpanded)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  {isRouteFormExpanded ? <IoChevronUp /> : <IoChevronDown />}
                  {isRouteFormExpanded ? 'Contraer' : 'Expandir'}
                </button>
              </div>

              {/* Route Search */}
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="w-48">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Modo de Búsqueda</label>
                    <select
                      value={routeSearchMode}
                      onChange={(e) => { 
                        setRouteSearchMode(e.target.value as 'search' | 'changeSource' | 'changeTarget') 
                        setIsRouteFormExpanded(true)
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="search">Cambiar Ruta</option>
                      <option value="changeSource">Cambiar Origen</option>
                      <option value="changeTarget">Cambiar Destino</option>
                    </select>
                  </div>
                  
                  {routeSearchMode === 'search' && (
                    <>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Buscar Ruta</label>
                        <input
                          type="text"
                          value={routeSearchQuery}
                          onChange={(e) => setRouteSearchQuery(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ingrese nombre o ID de la ruta..."
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={handleRouteSearch}
                          disabled={isSearchingRoutes}
                          className={cn(
                            "px-4 py-2 rounded-md text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed",
                            `bg_${_color} hover:bg_${_color}_dark`
                          )}
                        >
                          {isSearchingRoutes ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Buscando...
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <IoSearch />
                              Buscar
                            </div>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Expandable Route Form */}
              {isRouteFormExpanded && (
                <div className="mt-6 space-y-4">                  
                  {/* Source Configuration */}
                  {(routeSearchMode === 'search' || routeSearchMode === 'changeSource') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-md">
                      <div className="col-span-2">
                        <h4 className="text-md font-medium text-gray-700">Origen</h4>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                        <select
                          value={transferData.route.source.type}
                          onChange={(e) => handleEndpointTypeChange('route.source', e.target.value as "Source" | "Location")}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {endpointTypes.map(type => (
                            <option key={`source-type-${type}`} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar {transferData.route.source.type}</label>
                        {transferData.route.source.type === "Source" ? (
                          <CustomSearchSelect
                            value={(transferData.route.source.data as SourceData).id}
                            onChange={(value) => {
                              const selectedSource = sources.find(s => s.id === value);
                              if (selectedSource) {
                                handleEndpointDataSelect('route.source.data', {id: selectedSource.id, name: selectedSource.name});
                              }
                            }}
                            options={sources.map(source => ({
                              value: source.id,
                              label: `${source.name} ${source.name !== source.id ? '('+source.id+')' : ''}`
                            }))}
                            placeholder="Seleccionar Origen"
                            onSearch={setSourceQuery}
                          />
                        ) : (
                          <CustomSearchSelect
                            value={locations.findIndex(loc => 
                              loc.criteria0 === (transferData.route.source.data as LocationData).criteria0 && 
                              loc.criteria1 === (transferData.route.source.data as LocationData).criteria1 && 
                              loc.criteria2 === (transferData.route.source.data as LocationData).criteria2  && 
                              loc.criteria3 === (transferData.route.source.data as LocationData).criteria3
                            ).toString()}
                            onChange={(value) => {
                              const index = parseInt(value);
                              if (index >= 0) {
                                handleEndpointDataSelect('route.source.data', locations[index]);
                              }
                            }}
                            options={locations.map((location, index) => ({
                              value: index.toString(),
                              label: `${formatLocationName(location)}`
                            }))}
                            placeholder="Seleccionar Ubicación"
                            onSearch={setLocationQuery}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Target Configuration */}
                  {(routeSearchMode === 'search' || routeSearchMode === 'changeTarget') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-md">
                      <div className="col-span-2">
                        <h4 className="text-md font-medium text-gray-700">Destino</h4>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                        <select
                          value={transferData.route.target.type}
                          onChange={(e) => handleEndpointTypeChange('route.target', e.target.value as "Source" | "Location")}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {endpointTypes.map(type => (
                            <option key={`target-type-${type}`} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar {transferData.route.target.type}</label>
                        
                        {transferData.route.target.type === "Source" ? (
                          <CustomSearchSelect
                            value={(transferData.route.target.data as SourceData).id}
                            onChange={(value) => {
                              const selectedSource = sources.find(s => s.id === value);
                              if (selectedSource) {
                                handleEndpointDataSelect('route.target.data', {id: selectedSource.id, name: selectedSource.name});
                              }
                            }}
                            options={sources.map(source => ({
                              value: source.id,
                              label: `${source.name} ${source.name !== source.id ? '('+source.id+')' : ''}`
                            }))}
                            placeholder="Seleccionar Destino"
                            onSearch={setSourceQuery}
                          />
                        ) : (
                          <CustomSearchSelect
                            value={locations.findIndex(loc => 
                              loc.criteria0 === (transferData.route.target.data as LocationData).criteria0 && 
                              loc.criteria1 === (transferData.route.target.data as LocationData).criteria1 && 
                              loc.criteria2 === (transferData.route.target.data as LocationData).criteria2 && 
                              loc.criteria3 === (transferData.route.target.data as LocationData).criteria3
                            ).toString()}
                            onChange={(value) => {
                              const index = parseInt(value);
                              if (index >= 0) {
                                handleEndpointDataSelect('route.target.data', locations[index]);
                              }
                            }}
                            options={locations.map((location, index) => ({
                              value: index.toString(),
                              label: `${formatLocationName(location)}`
                            }))}
                            placeholder="Seleccionar Ubicación"
                            onSearch={setLocationQuery}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Show selected route or search results */}
              {selectedRoute ? renderSelectedRoute() : renderRouteResults()}
            </div>

            {/* Courier Section */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-800">Nueva Información de Courier</h3>
                <button
                  type="button"
                  onClick={() => setIsCourierFormExpanded(!isCourierFormExpanded)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  {isCourierFormExpanded ? <IoChevronUp /> : <IoChevronDown />}
                  {isCourierFormExpanded ? 'Contraer' : 'Expandir'}
                </button>
              </div>

              {/* Courier Search */}
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Buscar Courier</label>
                    <input
                      type="text"
                      value={courierSearchQuery}
                      onChange={(e) => setCourierSearchQuery(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ingrese nombre o ID del courier..."
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleCourierSearch}
                      disabled={isSearchingCouriers}
                      className={cn(
                        "px-4 py-2 rounded-md text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed",
                        `bg_${_color} hover:bg_${_color}_dark`
                      )}
                    >
                      {isSearchingCouriers ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Buscando...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <IoSearch />
                          Buscar
                        </div>
                      )}
                    </button>
                  </div>
                </div>

              </div>
              
              {/* Expandable Courier Form */}
              {isCourierFormExpanded && (
                <div className="mt-6 space-y-4">

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                      <input
                        type="text"
                        value={transferData.courier.name}
                        onChange={(e) => handleInputChange('courier.name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Nombre del courier"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Alias</label>
                      <input
                        type="text"
                        value={transferData.courier.alias}
                        onChange={(e) => handleInputChange('courier.alias', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Alias del courier"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Envío</label>
                      <select
                        value={transferData.courier.shippingType}
                        onChange={(e) => handleInputChange('courier.shippingType', e.target.value)}
                        className={cn(
                          "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                          { "text-gray-500": transferData.courier.shippingType === "" }
                        )}
                      >
                        <option value="" disabled hidden className="text-gray-500">Seleccionar tipo de envío</option>
                        {shippingTypes.map(type => (
                          <option className="text-gray-700" key={`shipping-${type}`} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Entrega</label>
                      <select
                        value={transferData.courier.deliveryType}
                        onChange={(e) => handleInputChange('courier.deliveryType', e.target.value)}
                        className={cn(
                          "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                          { "text-gray-500": transferData.courier.deliveryType === "" }
                        )}
                      >
                        <option value="" disabled hidden className="text-gray-500">Seleccionar tipo de entrega</option>
                        {deliveryTypes.map(type => (
                          <option className="text-gray-700" key={`delivery-${type}`} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Canal</label>
                      <CustomSearchSelect
                        value={transferData.courier.channel}
                        onChange={(value) => handleInputChange('courier.channel', value)}
                        options={channels.map(channel => ({
                          value: channel.id,
                          label: channel.name
                        }))}
                        placeholder="Seleccionar canal"
                        onSearch={setChannelQuery}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Show selected courier or search results */}
              {selectedCourier ? renderSelectedCourier() : renderCourierResults()}
            </div>

            {/* Leadtime Section */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Tiempo de Entrega</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                  <input
                    type="number"
                    min="1"
                    value={transferData.leadtime.value}
                    onChange={(e) => handleInputChange('leadtime.value', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
                  <select
                    value={transferData.leadtime.unit}
                    onChange={(e) => handleInputChange('leadtime.unit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="d">Días</option>
                    <option value="h">Horas</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaTimes className="mr-2 -ml-1 h-4 w-4" />
                Cancelar
              </button>
              <button
                type="submit"
                className={cn(
                  "inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2",
                  `bg_${_color} hover:bg_${_color}_dark focus:ring_${_color}`
                )}
              >
                <FaSave className="mr-2 -ml-1 h-4 w-4" />
                Transferir Coberturas
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
